import { MinimalRelationship } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type UpdateRelationshipReferencesOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  relationships: MinimalRelationship[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: string) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch?: (db: NoInfer<TDb>) => {
    update: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

const BATCH_SIZE = 500;

export const updateRelationshipReferences = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: UpdateRelationshipReferencesOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TDb
  >,
): Promise<void> => {
  const {
    relationships,
    collection,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  if (!relationships.length) {
    return;
  }

  const relationshipsCollection = collection(
    db,
    FirestoreCollections.Relationships,
  );

  if (!writeBatch) {
    relationships.forEach(({ id, references }) => {
      detachWrite(
        updateDoc(doc(relationshipsCollection, id), {
          references,
          updatedAt: serverTimestamp(),
        }),
        onWriteError,
      );
    });
    return;
  }

  const batchCount = Math.ceil(relationships.length / BATCH_SIZE);
  const batches = Array.from({ length: batchCount }, () => writeBatch(db));

  relationships.forEach(({ id, references }, index) => {
    const batchIndex = Math.floor(index / BATCH_SIZE);
    batches[batchIndex].update(doc(relationshipsCollection, id), {
      references,
      updatedAt: serverTimestamp(),
    });
  });

  batches.forEach((batch) => {
    detachWrite(batch.commit(), onWriteError);
  });
};
